import { Children, ReactNode, useEffect, useRef, useState } from "react";
import { DateTime } from 'luxon';
import { DropDown, Padder, SimpleButton, SimpleContainer, SimpleLinkButton, SimplePageTemplate } from "./SimpleElements"
import { useOrganization } from "../../../biz/hooks/entities/useOrganization";
import { AdItemType, AdProjectType, AdProjectUIState, AdStateType } from "../../../biz/data/redux/organizationReducer";
import { AdPreview } from "./Ads/AdPreviews";
import { AdPackageTopSelectorType, useAdsSettings, useAdsUI, useAdsUISettings } from "./Ads/useAds";
import { useResizeImage } from "../../../biz/data/io/useResizeImage";
import { useDateFunctions, useFormatting } from "../../../biz/translations/useFormatting";
import { usePlant, usePlantReducer, usePlantUI } from "../../../biz/hooks/usePlantHooks";
import { useLocation, useNavigate } from "react-router-dom";
import { Config } from "../../../biz/data/Config";
import { DataHelper } from "../../../biz/data/Helpers";
import { Console } from "console";
import { PortalContainer } from "../main/Portal";
import { AdPackageType, usePricing } from "../../../biz/pricing/usePricing";
import { useFlagsLocal } from "../../../biz/hooks/ui/useFlagsLocal";
import { useDialogs } from "./MesssageBoxes";
import { useAppData } from "../../../biz/data/useAppData";
import { Trans, useTranslation } from "react-i18next";

export const SimpleAds = () => {

    const { t } = useTranslation();
    const org = useOrganization();
    const proStatus = org.currentOrganizationProStatus;

    const ui = useAdsUI();

    const selectedTab = ui.selectedTab ?? (ui.adsInStateDrafting.length > 0 ? "drafting" :
        ui.adsInStateActive.length > 0 ? "active" :
            ui.adsInStateFinished.length > 0 ? "finished" : "drafting");

    const onUpdate = async (adId: string, model: AdItemType, file: File | undefined) => {
        const resp = await ui.update(org?.currentOrganization?.id ?? "", adId, model, file);
        if (resp?.success) {
            await ui.reload();
        }
        return true;
    }

    const onStateChange = async (adId: string, state: AdStateType) => {
        const resp = await ui.changeState(org?.currentOrganization?.id ?? "", adId, state);
        if (resp?.success) {
            await ui.reload();
        }
        return true;
    }


    return (
        <div>
            {proStatus === "pro" && (
                <AdPackageTopSelector
                    selected={selectedTab}
                    onClick={e => ui.updateUI.setSelectedTab(e)}
                    draftingCount={ui.adsInStateDrafting.length ?? 0}
                    activeCount={ui.adsInStateActive.length ?? 0}
                    finishedCount={ui.adsInStateFinished.length ?? 0}
                />
            )}
            <SimplePageTemplate pageContainerClassName={selectedTab === "finished" ? "finished-ads" : ""}>
                {proStatus === "pro" && (
                    <div className={"admanager" + (ui.isWorking ? " working" : "")}>


                        {selectedTab === "drafting" && (
                            <div>
                                <AdsTop header={t("draft")} />

                                <div className="adslist">
                                    {ui.adsInStateDrafting?.map(adProject => (
                                        <div key={adProject?.id}>
                                            <AdPackageHeader
                                                adProject={adProject}
                                                onStateChange={async () => await onStateChange(adProject?.id, "for_review")}
                                                uistate={adProject?.adProjectUIState ?? "drafting"}
                                            >{adProject?.adLines?.map(ad => (
                                                <AdItem
                                                    key={ad?.id}
                                                    editable={true}
                                                    ad={ad}
                                                    onUpdate={onUpdate}
                                                    uistate={adProject?.adProjectUIState ?? "drafting"}
                                                />
                                            ))}
                                            </AdPackageHeader>
                                        </div>
                                    ))}
                                </div>
                            </div>)}

                        {(selectedTab === "active") && (

                            <div>
                                <AdsTop header={t("active")} />

                                <div className="adslist">
                                    {ui.adsInStateActive?.map(adProject => (
                                        <div key={adProject?.id}>
                                            <AdPackageHeader
                                                adProject={adProject}
                                                onStateChange={async () => await onStateChange(adProject?.id, "for_review")}
                                                uistate={adProject?.adProjectUIState ?? "drafting"}
                                            >
                                                {adProject?.adLines?.map(ad => (
                                                    <AdItem
                                                        key={ad?.id}
                                                        editable={true}
                                                        ad={ad}
                                                        onUpdate={onUpdate}
                                                        uistate={adProject?.adProjectUIState ?? "drafting"}
                                                    />
                                                ))}
                                                <ProjectAdditionalServices
                                                    adProject={adProject}
                                                    showStats={true}
                                                />
                                            </AdPackageHeader>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedTab === "finished" && (

                            <div>
                                <AdsTop header={t("finished")} selectedTab={selectedTab} />
                                <div className="adslist">




                                    {ui.adsInStateFinished?.map(adProject => (
                                        <FinishedPackage key={adProject?.id} adProject={adProject} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {proStatus !== "pro" && (
                    <div className="admanager">
                        <AdsTop header={t("advertisements")} />
                    </div>
                )}

                <DefaultBanner
                    title={t("advertisements")}
                    text={t("advertisements_banner_text")}
                    image="./gfx/banner.ads.png"
                />
                {/* 
                <Padder heightPx={128} /> */}
            </SimplePageTemplate>
            <Padder heightPx={48} />
        </div>
    )
}

type StatsType = {
    num_views?: number,
    num_press?: number,
    num_users_view?: number,
    num_users_press?: number,
    stats_updated_epoch?: number
}
const FinishedPackage = ({ adProject }: { adProject: AdProjectType, }) => {


    const f = useFormatting();
    const df = useDateFunctions();
    const { t } = useTranslation();

    const [adLines, setAdLines] = useState<AdItemType[]>();


    const periodName = t("periodXtoY", {
        from: f.datekeyToDate(adProject?.startdatekey),
        to: f.datekeyToDate(adProject?.enddatekey)
    });

    useEffect(() => {
        calcValues();

        if (adProject?.adLines?.length > 0) {
            let l = adProject?.adLines ?? [];
            l.sort((a, b) => {
                if (a.startDateKey > b.startDateKey) { return -1; }
                else if (a.startDateKey > b.startDateKey) { return 1; }
                else { return 0; }
            })
            setAdLines(l);
        }

    }, [, adProject])


    const [stats, setStats] = useState<StatsType>({});
    const calcValues = () => {
        const ads = adProject.adLines;
        let d = { num_views: 0, num_press: 0, num_users_view: 0, num_users_press: 0 };
        ads.forEach(a => {
            if (a.num_views > 0) { d.num_views += a.num_views; }
            if (a.num_press > 0) { d.num_press += a.num_press; }
            if (a.num_users_view > 0) { d.num_users_view += a.num_users_view; }
            if (a.num_users_press > 0) { d.num_users_press += a.num_users_press; }
        });

        if (adProject.hasinstagramstory === true) {
            d.num_views += (adProject?.instagramstory_num_views ?? 0);
            d.num_press += (adProject?.instagramstory_num_press ?? 0);
            d.num_users_view += (adProject?.instagramstory_num_views ?? 0);
            d.num_users_press += (adProject?.instagramstory_num_press ?? 0);
        }

        if (adProject.hasinstagrampost === true) {
            d.num_views += (adProject?.instagrampost_num_views ?? 0);
            d.num_press += (adProject?.instagrampost_num_press ?? 0);
            d.num_users_view += (adProject?.instagrampost_num_views ?? 0);
            d.num_users_press += (adProject?.instagrampost_num_press ?? 0);
        }

        if (adProject.hassponsoredpost === true) {
            d.num_views += (adProject?.sponsoredpost_num_views ?? 0);
            d.num_press += (adProject?.sponsoredpost_num_press ?? 0);
            d.num_users_view += (adProject?.sponsoredpost_num_views ?? 0);
            d.num_users_press += (adProject?.sponsoredpost_num_press ?? 0);
        }

        if (adProject.hassponsoredblog === true) {
            d.num_views += (adProject?.sponsoredblog_num_views ?? 0);
            d.num_press += (adProject?.sponsoredblog_num_press ?? 0);
            d.num_users_view += (adProject?.sponsoredblog_num_views ?? 0);
            d.num_users_press += (adProject?.sponsoredblog_num_press ?? 0);
        }

        if (adProject.haspushnotification === true) {
            d.num_views += (adProject?.pushnotification_num_views ?? 0);
            d.num_press += (adProject?.pushnotification_num_press ?? 0);
            d.num_users_view += (adProject?.pushnotification_num_views ?? 0);
            d.num_users_press += (adProject?.pushnotification_num_press ?? 0);
        }

        setStats(d);
    }

    const [expanded, setExpanded] = useState<boolean>(false);

    const onExpand = () => {
        setExpanded(!expanded);
    }


    return (
        <div style={{ position: "relative" }} className="adpackage-card-finished">
            <div className="adpackage-card-finished-header">
                <AdPackageHeaderItem label={t("adPackageX", { name: adProject?.packagename })} value={adProject?.name} />
                <AdPackageHeaderItem label={t("period")} value={periodName} />

                <div className="adpackage-card-finished-summary">
                    <div className="acfs-total">
                        {t("total")}
                    </div>
                    <div className="acfs-data">
                        <div className="acfs-label">{t("views")}</div>
                        <div className="acfs-value">{f.intToSeparatedString(stats?.num_views ?? 0)}</div>
                    </div>
                    <div className="acfs-data">
                        <div className="acfs-label">{t("clicks")}</div>
                        <div className="acfs-value">{f.intToSeparatedString(stats?.num_press ?? 0)}</div>
                    </div>
                    <div className="acfs-data">
                        <div className="acfs-label">CTR</div>
                        <div className="acfs-value">
                            {(stats?.num_views ?? 0) > 0 && (
                                <span>
                                    {f.floatToPercent((stats?.num_press ?? 0) / (stats?.num_views ?? 0))}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {expanded && (
                <div>
                    {adLines?.map(ad => (
                        <AdItem
                            key={ad?.id}
                            editable={false}
                            ad={ad}
                            onUpdate={undefined}
                            uistate={"finished"}
                        />
                    ))}
                </div>
            )}

            <div className="adpackage-card-finished-btn" onClick={onExpand}>
                {!expanded && (
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28Z" fill="#ECE5DA" />
                        <path d="M27.3359 33.6641L19.8359 26.1641C19.4453 25.8125 19.4453 25.2266 19.8359 24.8359C20.1875 24.4844 20.7734 24.4844 21.1641 24.8359L28 31.7109L34.8359 24.875C35.1875 24.4844 35.7734 24.4844 36.1641 24.875C36.5156 25.2266 36.5156 25.8125 36.1641 26.1641L28.625 33.6641C28.2734 34.0547 27.6875 34.0547 27.3359 33.6641Z" fill="#4E3D22" />
                    </svg>
                )}
                {expanded && (
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28Z" fill="#ECE5DA" />
                        <path d="M27.3359 22.3359C27.6875 21.9844 28.2734 21.9844 28.625 22.3359L36.1641 29.8359C36.5156 30.2266 36.5156 30.8125 36.1641 31.1641C35.7734 31.5547 35.1875 31.5547 34.8359 31.1641L28 24.3281L21.1641 31.1641C20.7734 31.5547 20.1875 31.5547 19.8359 31.1641C19.4453 30.8125 19.4453 30.2266 19.8359 29.875L27.3359 22.3359Z" fill="#4E3D22" />
                    </svg>

                )}
            </div>
        </div>
    )
}


const AdsTop = ({ header, selectedTab }: { header: string, selectedTab?: AdPackageTopSelectorType }) => {

    const { t } = useTranslation();
    const [addAdDialogVisible, setAddAdDialogVisible] = useState<boolean>(false);

    const dialog = useDialogs();
    const helloEmail = useAppData().helloEmail;

    return (
        <div className="top">
            <div className="top-items">
                <h1>{header}</h1>

                {selectedTab !== "finished" && (
                    <>
                        <SimpleButton
                            onClick={() => setAddAdDialogVisible(true)}
                            label={t("addAdPackage")}
                            type="secondarybeige20"
                            icon="plus"
                            shape="none"
                        />

                        <SimpleButton
                            onClick={() => {
                                dialog.showInfo(
                                    t("welcomeWithQuestions"),
                                    t("welcomeWithQuestionsDescription"),
                                    { okText: helloEmail, okIcon: "email" })
                            }}
                            label={t("help")}
                            type="tertiarybeige50"
                            icon="info"
                            shape="none"
                        />

                        <DropDown />
                    </>
                )}
            </div>
            <Padder heightPx={32} />
            {selectedTab !== "finished" && (
                <div className="sep"></div>
            )}
            <Padder heightPx={32} />
            <AdDialog visible={addAdDialogVisible} onSetVisible={e => { setAddAdDialogVisible(e) }} />
        </div>
    )
}


const ProjectAdditionalServices = ({ adProject, showStats }: { adProject: AdProjectType, showStats: boolean }) => {
    const { t } = useTranslation();
    return (
        <div>

            {!!adProject?.hassponsoredpost && (
                <AdPackageFeaturePost
                    header={t("ad_desc_sponsored_post_header")}
                    tagline={t("ad_desc_sponsored_post_tagline")}
                    svgElement={(<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M14.5 28.0625C16.125 28.6875 18 29 20 29C27.75 29 33 24 33 19C33 14.0625 27.75 9 20 9C12.1875 9 7.00001 14.0625 7.00001 19C7.00001 21 7.75001 22.9375 9.18751 24.625C9.75001 25.1875 10 26 9.93751 26.8125C9.87501 27.9375 9.56251 29 9.25001 29.875C10.3125 29.4375 11.1875 28.875 11.6875 28.5C12.5 27.875 13.5625 27.75 14.5 28.0625ZM5.62501 30.5C6.25001 29.5 6.81251 28.125 6.93751 26.5625C5.06251 24.4375 4.00001 21.875 4.00001 19C4.00001 11.875 11.125 6 20 6C28.8125 6 36 11.875 36 19C36 26.1875 28.8125 32 20 32C17.6875 32 15.5 31.625 13.5 30.9375C12.75 31.4375 11.5 32.1875 10.0625 32.8125C9.12501 33.25 8.06251 33.625 6.93751 33.8125C6.93751 33.8125 6.87501 33.875 6.81251 33.875C6.56251 33.9375 6.25001 33.9375 6.00001 34H5.93751C5.62501 34 5.31251 34.0625 5.00001 34.0625C4.56251 34.0625 4.18751 33.8125 4.06251 33.4375C3.87501 33.0625 4.00001 32.625 4.25001 32.3125C4.50001 32.0625 4.75001 31.75 5.00001 31.5C5.06251 31.3125 5.18751 31.1875 5.25001 31.0625L5.31251 31C5.43751 30.875 5.50001 30.6875 5.62501 30.5ZM16 17C14.875 17 14 16.125 14 15C14 13.9375 14.875 13 16 13C17.0625 13 18 13.9375 18 15C18 16.125 17.0625 17 16 17ZM24 17C22.875 17 22 16.125 22 15C22 13.9375 22.875 13 24 13C25.0625 13 26 13.9375 26 15C26 16.125 25.0625 17 24 17ZM13.375 20.9375C14 20.375 14.9375 20.4375 15.5 21C16.5625 22.25 18.1875 23 19.9375 23C21.75 23 23.3125 22.25 24.4375 21C25 20.4375 25.9375 20.375 26.5625 20.9375C27.1875 21.4375 27.25 22.4375 26.6875 23C25 24.875 22.625 26 19.9375 26C17.3125 26 14.875 24.875 13.25 23C12.6875 22.4375 12.75 21.4375 13.375 20.9375Z" fill="#9F8E73" />
                    </svg>)}
                    showStats={showStats}
                    num_views={adProject?.sponsoredpost_num_views ?? 0}
                    num_press={adProject?.sponsoredpost_num_press ?? 0}
                />
            )}
            {!!adProject?.hassponsoredblog && (
                <AdPackageFeaturePost
                    header={t("ad_desc_sponsored_blog_header")}
                    tagline={t("ad_desc_sponsored_blog_tagline")}
                    info={t("ad_desc_additional_fee_for_selected_blogger_writer")}
                    svgElement={(<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M20 7V33H31C31.5 33 32 32.5625 32 32V8C32 7.5 31.5 7 31 7H20ZM17 7H13C12.4375 7 12 7.5 12 8V10H13.5C14.3125 10 15 10.6875 15 11.5C15 12.375 14.3125 13 13.5 13H12V18.5H13.5C14.3125 18.5 15 19.1875 15 20C15 20.875 14.3125 21.5 13.5 21.5H12V27H13.5C14.3125 27 15 27.6875 15 28.5C15 29.375 14.3125 30 13.5 30H12V32C12 32.5625 12.4375 33 13 33H17V7ZM9 30H6.5C5.625 30 5 29.375 5 28.5C5 27.6875 5.625 27 6.5 27H9V21.5H6.5C5.625 21.5 5 20.875 5 20C5 19.1875 5.625 18.5 6.5 18.5H9V13H6.5C5.625 13 5 12.375 5 11.5C5 10.6875 5.625 10 6.5 10H9V8C9 5.8125 10.75 4 13 4H31C33.1875 4 35 5.8125 35 8V32C35 34.25 33.1875 36 31 36H13C10.75 36 9 34.25 9 32V30Z" fill="#9F8E73" />
                    </svg>)}
                    showStats={showStats}
                    num_views={adProject?.sponsoredblog_num_views ?? 0}
                    num_press={adProject?.sponsoredblog_num_press ?? 0}
                />
            )}
            {!!adProject?.haspushnotification && (
                <AdPackageFeaturePost
                    header={t("ad_desc_sponsored_push_header")}
                    tagline={t("ad_desc_sponsored_push_tagline")}
                    svgElement={(<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M16.5 9.125C16.125 9.75 15.4375 10.0625 14.75 9.875C13.375 9.4375 11.875 9.75 10.75 10.8125C9.6875 11.875 9.375 13.4375 9.8125 14.75C10 15.4375 9.6875 16.1875 9.0625 16.5625C7.8125 17.1875 7 18.5 7 20C7 21.5625 7.8125 22.875 9.0625 23.5C9.6875 23.875 10 24.5625 9.8125 25.25C9.375 26.625 9.75 28.125 10.8125 29.1875C11.875 30.25 13.375 30.625 14.75 30.1875C15.4375 30 16.1875 30.3125 16.5 30.9375C17.1875 32.1875 18.5 33 20 33C21.5 33 22.8125 32.1875 23.4375 30.9375C23.8125 30.3125 24.5625 30 25.25 30.1875C26.5625 30.625 28.125 30.25 29.1875 29.1875C30.25 28.125 30.5625 26.625 30.125 25.25C29.9375 24.5625 30.25 23.8125 30.875 23.5C32.125 22.8125 33 21.5 33 20C33 18.5 32.125 17.1875 30.875 16.5625C30.25 16.1875 29.9375 15.4375 30.125 14.75C30.5625 13.4375 30.25 11.875 29.1875 10.8125C28.125 9.75 26.5625 9.4375 25.25 9.875C24.5625 10.0625 23.8125 9.75 23.4375 9.125C22.8125 7.875 21.5 7 20 7C18.4375 7 17.125 7.875 16.5 9.125ZM20 4C22.1875 4 24.1875 5.0625 25.5 6.75C27.5625 6.4375 29.6875 7.125 31.3125 8.6875C32.875 10.3125 33.5 12.4375 33.25 14.5C34.9375 15.8125 36 17.8125 36 20C36 22.25 34.9375 24.25 33.25 25.5C33.5625 27.5625 32.875 29.75 31.3125 31.3125C29.6875 32.9375 27.5625 33.5625 25.5 33.3125C24.1875 34.9375 22.1875 36 20 36C17.75 36 15.75 34.9375 14.5 33.3125C12.4375 33.5625 10.25 32.9375 8.6875 31.375C7.0625 29.75 6.4375 27.625 6.6875 25.5625C5.0625 24.25 4 22.25 4 20C4 17.8125 5.0625 15.8125 6.6875 14.5C6.4375 12.4375 7.0625 10.3125 8.625 8.6875C10.25 7.125 12.375 6.5 14.4375 6.75C15.75 5.0625 17.75 4 20 4ZM16 18C14.875 18 14 17.125 14 16C14 14.9375 14.875 14 16 14C17.0625 14 18 14.9375 18 16C18 17.125 17.0625 18 16 18ZM26 24C26 25.125 25.0625 26 24 26C22.875 26 22 25.125 22 24C22 22.9375 22.875 22 24 22C25.0625 22 26 22.9375 26 24ZM25.0625 17.0625L17 25.0625C16.4375 25.6875 15.5 25.6875 14.9375 25.0625C14.3125 24.5 14.3125 23.5625 14.9375 22.9375L22.9375 15C23.5 14.375 24.4375 14.375 25.0625 15C25.625 15.5625 25.625 16.5 25.0625 17.0625Z" fill="#9F8E73" />
                    </svg>)}
                    showStats={showStats}
                    num_views={adProject?.pushnotification_num_views ?? 0}
                    num_press={adProject?.pushnotification_num_press ?? 0}
                />
            )}
            {!!adProject?.hasinstagramstory && (
                <AdPackageFeaturePost
                    header={t("ad_desc_sponsored_instagramstories_header")}
                    tagline={t("ad_desc_sponsored_instagramstories_tagline")}
                    svgElement={(<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M20.0312 12.7812C23.9688 12.7812 27.2188 16.0312 27.2188 19.9688C27.2188 23.9688 23.9688 27.1562 20.0312 27.1562C16.0312 27.1562 12.8438 23.9688 12.8438 19.9688C12.8438 16.0312 16.0312 12.7812 20.0312 12.7812ZM20.0312 24.6562C22.5938 24.6562 24.6562 22.5938 24.6562 19.9688C24.6562 17.4062 22.5938 15.3438 20.0312 15.3438C17.4062 15.3438 15.3438 17.4062 15.3438 19.9688C15.3438 22.5938 17.4688 24.6562 20.0312 24.6562ZM29.1562 12.5312C29.1562 11.5938 28.4062 10.8438 27.4688 10.8438C26.5312 10.8438 25.7812 11.5938 25.7812 12.5312C25.7812 13.4688 26.5312 14.2188 27.4688 14.2188C28.4062 14.2188 29.1562 13.4688 29.1562 12.5312ZM33.9062 14.2188C34.0312 16.5312 34.0312 23.4688 33.9062 25.7812C33.7812 28.0312 33.2812 29.9688 31.6562 31.6562C30.0312 33.2812 28.0312 33.7812 25.7812 33.9062C23.4688 34.0312 16.5312 34.0312 14.2188 33.9062C11.9688 33.7812 10.0312 33.2812 8.34375 31.6562C6.71875 29.9688 6.21875 28.0312 6.09375 25.7812C5.96875 23.4688 5.96875 16.5312 6.09375 14.2188C6.21875 11.9688 6.71875 9.96875 8.34375 8.34375C10.0312 6.71875 11.9688 6.21875 14.2188 6.09375C16.5312 5.96875 23.4688 5.96875 25.7812 6.09375C28.0312 6.21875 30.0312 6.71875 31.6562 8.34375C33.2812 9.96875 33.7812 11.9688 33.9062 14.2188ZM30.9062 28.2188C31.6562 26.4062 31.4688 22.0312 31.4688 19.9688C31.4688 17.9688 31.6562 13.5938 30.9062 11.7188C30.4062 10.5312 29.4688 9.53125 28.2812 9.09375C26.4062 8.34375 22.0312 8.53125 20.0312 8.53125C17.9688 8.53125 13.5938 8.34375 11.7812 9.09375C10.5312 9.59375 9.59375 10.5312 9.09375 11.7188C8.34375 13.5938 8.53125 17.9688 8.53125 19.9688C8.53125 22.0312 8.34375 26.4062 9.09375 28.2188C9.59375 29.4688 10.5312 30.4062 11.7812 30.9062C13.5938 31.6562 17.9688 31.4688 20.0312 31.4688C22.0312 31.4688 26.4062 31.6562 28.2812 30.9062C29.4688 30.4062 30.4688 29.4688 30.9062 28.2188Z" fill="#9F8E73" />
                    </svg>)}
                    showStats={showStats}
                    num_views={adProject?.instagramstory_num_views ?? 0}
                    num_press={adProject?.instagramstory_num_press ?? 0}
                />
            )}
            {!!adProject?.hasinstagrampost && (
                <AdPackageFeaturePost
                    header={t("ad_desc_sponsored_instagrampost_header")}
                    tagline={t("ad_desc_sponsored_instagrampost_tagline")}
                    svgElement={(<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                        <path d="M20.0312 12.7812C23.9688 12.7812 27.2188 16.0312 27.2188 19.9688C27.2188 23.9688 23.9688 27.1562 20.0312 27.1562C16.0312 27.1562 12.8438 23.9688 12.8438 19.9688C12.8438 16.0312 16.0312 12.7812 20.0312 12.7812ZM20.0312 24.6562C22.5938 24.6562 24.6562 22.5938 24.6562 19.9688C24.6562 17.4062 22.5938 15.3438 20.0312 15.3438C17.4062 15.3438 15.3438 17.4062 15.3438 19.9688C15.3438 22.5938 17.4688 24.6562 20.0312 24.6562ZM29.1562 12.5312C29.1562 11.5938 28.4062 10.8438 27.4688 10.8438C26.5312 10.8438 25.7812 11.5938 25.7812 12.5312C25.7812 13.4688 26.5312 14.2188 27.4688 14.2188C28.4062 14.2188 29.1562 13.4688 29.1562 12.5312ZM33.9062 14.2188C34.0312 16.5312 34.0312 23.4688 33.9062 25.7812C33.7812 28.0312 33.2812 29.9688 31.6562 31.6562C30.0312 33.2812 28.0312 33.7812 25.7812 33.9062C23.4688 34.0312 16.5312 34.0312 14.2188 33.9062C11.9688 33.7812 10.0312 33.2812 8.34375 31.6562C6.71875 29.9688 6.21875 28.0312 6.09375 25.7812C5.96875 23.4688 5.96875 16.5312 6.09375 14.2188C6.21875 11.9688 6.71875 9.96875 8.34375 8.34375C10.0312 6.71875 11.9688 6.21875 14.2188 6.09375C16.5312 5.96875 23.4688 5.96875 25.7812 6.09375C28.0312 6.21875 30.0312 6.71875 31.6562 8.34375C33.2812 9.96875 33.7812 11.9688 33.9062 14.2188ZM30.9062 28.2188C31.6562 26.4062 31.4688 22.0312 31.4688 19.9688C31.4688 17.9688 31.6562 13.5938 30.9062 11.7188C30.4062 10.5312 29.4688 9.53125 28.2812 9.09375C26.4062 8.34375 22.0312 8.53125 20.0312 8.53125C17.9688 8.53125 13.5938 8.34375 11.7812 9.09375C10.5312 9.59375 9.59375 10.5312 9.09375 11.7188C8.34375 13.5938 8.53125 17.9688 8.53125 19.9688C8.53125 22.0312 8.34375 26.4062 9.09375 28.2188C9.59375 29.4688 10.5312 30.4062 11.7812 30.9062C13.5938 31.6562 17.9688 31.4688 20.0312 31.4688C22.0312 31.4688 26.4062 31.6562 28.2812 30.9062C29.4688 30.4062 30.4688 29.4688 30.9062 28.2188Z" fill="#9F8E73" />
                    </svg>)}
                    showStats={showStats}
                    num_views={adProject?.instagrampost_num_views ?? 0}
                    num_press={adProject?.instagrampost_num_press ?? 0}
                />
            )}
        </div>
    )
}


export const DefaultBanner = ({ title, text, image, imageDoubleSize }: { title: string, text: string, image: string, imageDoubleSize?: boolean }) => {
    return (
        <div className="empty-state-banner">
            <div className="bottom">
                <div className={"img" + (imageDoubleSize ? " img-2x" : "")}>
                    <img src={image} />
                </div>
                <div>
                    <Padder heightPx={30} />
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
                            <path d="M13.0195 4.875C10.0898 4.875 7.43359 6.4375 5.94922 8.9375C4.50391 11.4766 4.50391 14.5625 5.94922 17.0625C7.43359 19.6016 10.0898 21.125 13.0195 21.125C15.9102 21.125 18.5664 19.6016 20.0508 17.0625C21.4961 14.5625 21.4961 11.4766 20.0508 8.9375C18.5664 6.4375 15.9102 4.875 13.0195 4.875ZM13.0195 23C9.42578 23 6.14453 21.125 4.34766 18C2.55078 14.9141 2.55078 11.125 4.34766 8C6.14453 4.91406 9.42578 3 13.0195 3C16.5742 3 19.8555 4.91406 21.6523 8C23.4492 11.125 23.4492 14.9141 21.6523 18C19.8555 21.125 16.5742 23 13.0195 23ZM11.457 16.125H12.3945V13.625H11.457C10.9102 13.625 10.5195 13.2344 10.5195 12.6875C10.5195 12.1797 10.9102 11.75 11.457 11.75H13.332C13.8398 11.75 14.2695 12.1797 14.2695 12.6875V16.125H14.582C15.0898 16.125 15.5195 16.5547 15.5195 17.0625C15.5195 17.6094 15.0898 18 14.582 18H11.457C10.9102 18 10.5195 17.6094 10.5195 17.0625C10.5195 16.5547 10.9102 16.125 11.457 16.125ZM13.0195 10.5C12.3164 10.5 11.7695 9.95312 11.7695 9.25C11.7695 8.58594 12.3164 8 13.0195 8C13.6836 8 14.2695 8.58594 14.2695 9.25C14.2695 9.95312 13.6836 10.5 13.0195 10.5Z" fill="#9F8E73" />
                        </svg>
                    </div>
                    <h1 className="header">{title}</h1>
                    <Padder heightPx={16} />
                    <div className="text">
                        {text}
                    </div>
                    <Padder heightPx={80} />
                </div>
            </div>
        </div>
    )
}

const AdDialog = ({ visible, onSetVisible }: { visible?: boolean, onSetVisible(e: boolean): void }) => {
    const flags = useFlagsLocal();
    const { t } = useTranslation();

    const prices = usePricing();

    const small = prices.adPackages.find(m => m?.id === "small") as AdPackageType;
    const medium = prices.adPackages.find(m => m?.id === "medium") as AdPackageType;
    const large = prices.adPackages.find(m => m?.id === "large") as AdPackageType;

    const onOrderClick = (e: AdPackageType) => {
        var subject = "Beställning av annonspaket " + e?.name;
        var body = "Vi önskar beställa annonspaket " + e?.name + " för " + e?.price + ".\n\nÖnskad period: \n\nÖvriga önskemål:";
        var uri = "mailto:?subject=";
        uri += encodeURIComponent(subject);
        uri += "&body=";
        uri += encodeURIComponent(body);
        window.location.href = uri;
    }
    const helloEmail = useAppData().helloEmail;


    if (visible !== true) {
        return (null)
    }

    return (
        <PortalContainer visible={true} clickOutside={() => onSetVisible(false)}>
            <div className="ad-dialog">
                <div className="header">
                    {t("adDialog_chooseAdPackage")}
                    <span className="tagline">
                        <Trans
                            i18nKey="orEmailXForMoreHelpAndQuestions"
                            components={{
                                email: (<a href={"mailto:" + helloEmail}>{helloEmail}</a>)
                            }}
                        />
                    </span>
                </div>
                <div className="ad-dialog-columns">
                    <AdDialogColumn
                        header={small?.name}
                        tagline={"För er som vill synas hos en bred målgrupp i appen"} exposures={small?.exposures} price={small?.price}
                        onButtonClick={() => onOrderClick(small)}
                        bulletListHeader="Vad som ingår:"
                    >
                        <AdDialogBullet text={"Annons i Upptäck"} />
                        <AdDialogBullet text={"Annons i sökresultat"} />
                        <AdDialogBullet text={"Annons för tio utvalda växtporträtt"} />
                        <AdDialogBullet text={"Annons i Kalendern"} />
                        <AdDialogBullet text={"Statistikrapport & analys"} />
                        <AdDialogBullet text={"Erbjudande under Upptäck"} />
                    </AdDialogColumn>
                    <AdDialogColumn header={medium?.name} tagline={"För er som söker effektiv  synlighet på flera plattformar"} exposures={medium?.exposures} price={medium?.price}
                        onButtonClick={() => onOrderClick(medium)}
                        bulletListHeader="Allt från Litet, plus:"
                    >
                        <AdDialogBullet text={"Extra annons i Upptäck"} />
                        <AdDialogBullet text={"Annons i vårt flöde"} />
                        <AdDialogBullet text={"Banners på växtporträtt på sajt"} />
                        <AdDialogBullet text={"Samarbete instagramstories @ohgardenstories"} />
                    </AdDialogColumn>
                    <AdDialogColumn
                        header={large?.name} tagline={"För er som vill göra ett rejält avtryck! "} exposures={large?.exposures} price={large?.price}
                        onButtonClick={() => onOrderClick(large)}
                        bulletListHeader="Allt från Mellan, plus:"
                    >
                        <AdDialogBullet text={"Sponsrat inlägg i appens flöde"} />
                        <AdDialogBullet text={"Annons i Månadens Att göra"} />
                        <AdDialogBullet text={"Instagram post @ohgardenstories"} />
                        <AdDialogBullet text={"Blogginlägg eller nativeartikel"} tagline="Tillkommer arvode till vald skribent" />
                        <AdDialogBullet text={"Push-meddelanden med erbjudande"} />


                        <Padder heightPx={12} />
                        <div className="bullet-list-custom">
                            <div className="bullet-list-custom-header">
                                Möjlighet att lägga till:
                            </div>
                            <Padder heightPx={24} />
                            <AdDialogBulletCustom text="Skräddarsydda lösningar" />
                            <AdDialogBulletCustom text="Influencer marketing på olika plattformar" />
                            <AdDialogBulletCustom text="Skapa tävlingar och jobba med achievements i appen" />
                        </div>

                    </AdDialogColumn>
                </div>

                <div className="close" onClick={() => { onSetVisible(false) }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                        <path d="M18.4766 7.85156L13.7891 12.5391L18.4375 17.1875C18.8281 17.5391 18.8281 18.125 18.4375 18.4766C18.0859 18.8672 17.5 18.8672 17.1484 18.4766L12.4609 13.8281L7.8125 18.4766C7.46094 18.8672 6.875 18.8672 6.52344 18.4766C6.13281 18.125 6.13281 17.5391 6.52344 17.1484L11.1719 12.5L6.52344 7.85156C6.13281 7.5 6.13281 6.91406 6.52344 6.52344C6.875 6.17188 7.46094 6.17188 7.85156 6.52344L12.5 11.2109L17.1484 6.5625C17.5 6.17188 18.0859 6.17188 18.4766 6.5625C18.8281 6.91406 18.8281 7.5 18.4766 7.85156Z" fill="#9F8E73" />
                    </svg>
                </div>
            </div>
        </PortalContainer>

    )
}
const AdDialogColumn = ({ header, tagline, bulletListHeader, exposures, price, children, onButtonClick }: { header: string, tagline: string, bulletListHeader: string, exposures: string, price: string, children: ReactNode, onButtonClick(): void }) => {
    const helloEmail = useAppData().helloEmail;
    return (
        <div className="ad-dialog-column">

            <div className="ad-dialog-column-header">
                {header}
            </div>
            <div className="ad-dialog-column-body">
                <div style={{ padding: "48px" }} className="ad-dialog-column-body-top" >
                    <div className="ad-dialog-column-body-tagline">{tagline}</div>
                    <Padder heightPx={16} />
                    <div style={{ display: "inline-block", textAlign: "center", }}>
                        <div className="ad-dialog-column-body-line"></div>
                    </div>
                    <Padder heightPx={16} />
                    <div className="ad-dialog-column-body-exposures-1">Exponeringar</div>
                    <div className="ad-dialog-column-body-exposures-2">{exposures}</div>
                    <Padder heightPx={46} />
                    <div className="ad-dialog-column-body-btn" onClick={onButtonClick}>
                        <span>
                            {price}
                        </span>
                        <span>
                            <svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 7.375C4.64844 7.375 4.375 7.6875 4.375 8V8.89844L11.0938 14.4062C11.9141 15.0703 13.0469 15.0703 13.8672 14.4062L20.625 8.89844V8C20.625 7.6875 20.3125 7.375 20 7.375H5ZM4.375 11.3203V18C4.375 18.3516 4.64844 18.625 5 18.625H20C20.3125 18.625 20.625 18.3516 20.625 18V11.3203L15.0781 15.8516C13.5547 17.1016 11.4062 17.1016 9.92188 15.8516L4.375 11.3203ZM2.5 8C2.5 6.63281 3.59375 5.5 5 5.5H20C21.3672 5.5 22.5 6.63281 22.5 8V18C22.5 19.4062 21.3672 20.5 20 20.5H5C3.59375 20.5 2.5 19.4062 2.5 18V8Z" fill="#D09386" />
                            </svg>
                        </span>
                    </div>
                    <Padder heightPx={8} />
                    <div className="ad-dialog-column-body-email">
                        E-Post: {helloEmail}
                    </div>
                </div>

                <div className="bullet-list">
                    <div className="bullet-list-header">
                        {bulletListHeader}
                    </div>
                    <Padder heightPx={18} />
                    {children}
                </div>
            </div>

        </div>
    )
}
const AdDialogBullet = ({ text, tagline }: { text: string, tagline?: string }) => {
    return (
        <div className="bullet">
            <div>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
                    <path d="M5.25166 13.7888C5.04436 13.7888 4.90615 13.7197 4.76795 13.5815L0.207302 8.74444C-0.0691006 8.46803 -0.0691006 8.05343 0.207302 7.77703C0.483704 7.50062 0.898325 7.50062 1.17473 7.77703L5.18255 11.9922L14.7875 0.245058C15.0639 -0.0313448 15.4785 -0.100444 15.7549 0.175958C16.0313 0.452361 16.1004 0.866965 15.824 1.14337L5.73535 13.4433C5.66625 13.7197 5.45896 13.7888 5.25166 13.7888Z" fill="#D09386" />
                </svg>
            </div>
            <div>
                <div className="bullet-text">
                    {text}
                </div>
                <div className="bullet-tagline">
                    {tagline}
                </div>
            </div>
        </div>
    )
}

const AdDialogBulletCustom = ({ text }: { text: string }) => {
    return (
        <div className="bullet-custom">
            <div>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.1903 1.1174C19.5895 0.566451 20.4105 0.566452 20.8097 1.1174L22.5777 3.55707C22.8845 3.9804 23.4655 4.09599 23.911 3.82228L26.478 2.24488C27.0577 1.88866 27.8161 2.20283 27.9742 2.86463L28.6739 5.79515C28.7953 6.30366 29.2879 6.63282 29.8042 6.5504L32.7794 6.07541C33.4513 5.96815 34.0319 6.54867 33.9246 7.22056L33.4496 10.1958C33.3672 10.7121 33.6963 11.2047 34.2048 11.3261L37.1354 12.0258C37.7972 12.1839 38.1113 12.9423 37.7551 13.522L36.1777 16.089C35.904 16.5345 36.0196 17.1155 36.4429 17.4223L38.8826 19.1903C39.4335 19.5895 39.4335 20.4105 38.8826 20.8097L36.4429 22.5777C36.0196 22.8845 35.904 23.4655 36.1777 23.911L37.7551 26.478C38.1113 27.0577 37.7972 27.8161 37.1354 27.9742L34.2048 28.6739C33.6963 28.7953 33.3672 29.2879 33.4496 29.8042L33.9246 32.7794C34.0319 33.4513 33.4513 34.0319 32.7794 33.9246L29.8042 33.4496C29.2879 33.3672 28.7953 33.6963 28.6739 34.2048L27.9742 37.1354C27.8161 37.7972 27.0577 38.1113 26.478 37.7551L23.911 36.1777C23.4655 35.904 22.8845 36.0196 22.5777 36.4429L20.8097 38.8826C20.4105 39.4335 19.5895 39.4335 19.1903 38.8826L17.4223 36.4429C17.1155 36.0196 16.5345 35.904 16.089 36.1777L13.522 37.7551C12.9423 38.1113 12.1839 37.7972 12.0258 37.1354L11.3261 34.2048C11.2047 33.6963 10.7121 33.3672 10.1958 33.4496L7.22056 33.9246C6.54866 34.0319 5.96815 33.4513 6.07541 32.7794L6.5504 29.8042C6.63282 29.2879 6.30366 28.7953 5.79515 28.6739L2.86463 27.9742C2.20283 27.8161 1.88866 27.0577 2.24488 26.478L3.82228 23.911C4.09599 23.4655 3.9804 22.8845 3.55707 22.5777L1.1174 20.8097C0.566451 20.4105 0.566452 19.5895 1.1174 19.1903L3.55707 17.4223C3.9804 17.1155 4.09599 16.5345 3.82228 16.089L2.24488 13.522C1.88866 12.9423 2.20283 12.1839 2.86463 12.0258L5.79515 11.3261C6.30366 11.2047 6.63282 10.7121 6.5504 10.1958L6.07541 7.22056C5.96815 6.54867 6.54867 5.96815 7.22056 6.07541L10.1958 6.5504C10.7121 6.63282 11.2047 6.30366 11.3261 5.79515L12.0258 2.86463C12.1839 2.20283 12.9423 1.88866 13.522 2.24488L16.089 3.82228C16.5345 4.09599 17.1155 3.9804 17.4223 3.55707L19.1903 1.1174Z" fill="#506750" />
                    <path d="M19.6958 27.3305C19.6958 27.7322 19.9636 28 20.3653 28C20.7669 28 21.0347 27.7322 21.0347 27.3305V20.7029H27.6626C28.0643 20.7029 28.332 20.3682 28.332 20.0335C28.332 19.6318 28.0643 19.364 27.6626 19.364H21.0347V12.6695C21.0347 12.2678 20.7669 12 20.3653 12C19.9636 12 19.6958 12.2678 19.6958 12.6695V19.364H13.0015C12.5998 19.364 12.332 19.6318 12.332 20.0335C12.332 20.4351 12.5998 20.7029 13.0015 20.7029H19.6958V27.3305Z" fill="#F7D5C9" />
                </svg>

            </div>
            <div className="bullet-custom-text">
                {text}
            </div>
        </div>
    )
}
// ******************************************************************************************
// ********************************** AdPackageTopSelector **********************************
// ******************************************************************************************
const AdPackageTopSelector = ({ selected = "drafting", onClick, draftingCount, activeCount, finishedCount }: { selected: AdPackageTopSelectorType, onClick(e: AdPackageTopSelectorType): void, draftingCount: number, activeCount: number, finishedCount: number }) => {

    // 

    const { t } = useTranslation();

    return (
        <div className="adselector-container">
            <div className="adselector">

                {/* <div className={"adselector-item"} onClick={() => onClick("drafting")}>
                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.8638 19.3305C11.8638 19.7322 12.1316 20 12.5332 20C12.9349 20 13.2027 19.7322 13.2027 19.3305V12.7029H19.8306C20.2322 12.7029 20.5 12.3682 20.5 12.0335C20.5 11.6318 20.2322 11.364 19.8306 11.364H13.2027V4.66946C13.2027 4.26778 12.9349 4 12.5332 4C12.1316 4 11.8638 4.26778 11.8638 4.66946V11.364H5.16946C4.76778 11.364 4.5 11.6318 4.5 12.0335C4.5 12.4351 4.76778 12.7029 5.16946 12.7029H11.8638V19.3305Z" fill="#74584C" />
                    </svg>
                    {lang.create_ads}
                </div> */}

                <div className={"adselector-item" + (selected === "drafting" ? " selected" : "") + (draftingCount === 0 ? " inactive" : "")}
                    onClick={() => draftingCount > 0 && onClick("drafting")}>
                    {t("draft")}
                </div>
                {/* <div className={"adselector-item" + (selected === "upcoming" ? " selected" : "")} onClick={() => onClick("upcoming")}>
                    Kommande
                </div> */}
                <div className={"adselector-item" + (selected === "active" ? " selected" : "") + (activeCount === 0 ? " inactive" : "")}
                    onClick={() => activeCount > 0 && onClick("active")}>
                    {t("active")}
                </div>
                <div className={"adselector-item" + (selected === "finished" ? " selected" : "") + (finishedCount === 0 ? " inactive" : "")} onClick={() =>
                    finishedCount > 0 && onClick("finished")}>
                    {t("finished")}
                </div>
            </div>
        </div>
    )
}

const AdPackageFeaturePost = ({ header, tagline, info, svgElement, showStats, num_views, num_press }: { header: string, tagline: string, info?: string, svgElement: ReactNode, showStats: boolean, num_views: number, num_press: number }) => {
    const { t } = useTranslation();
    return (
        <div className="adpackage-featurecard">
            <div className="adpackage-featurecard-icobox">
                {svgElement}
            </div>

            {!showStats && (
                <div className="adpackage-featurecard-texts">
                    <div className="adpackage-featurecard-texts">
                        <div className="header">
                            {header}
                        </div>
                        <div className="tagline">
                            {tagline}
                        </div>
                        {!!info && (
                            <div className="info">

                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.2002 8.86665C11.2002 8.42482 11.5584 8.06665 12.0002 8.06665C12.442 8.06665 12.8002 8.42482 12.8002 8.86665C12.8002 9.30848 12.442 9.66665 12.0002 9.66665C11.5584 9.66665 11.2002 9.30848 11.2002 8.86665Z" fill="#CE9882" />
                                    <path d="M12.0002 10.5333C12.3868 10.5333 12.7002 10.8467 12.7002 11.2333V15.2333C12.7002 15.6199 12.3868 15.9333 12.0002 15.9333C11.6136 15.9333 11.3002 15.6199 11.3002 15.2333V11.2333C11.3002 10.8467 11.6136 10.5333 12.0002 10.5333Z" fill="#CE9882" />
                                    <path fillRule="evenodd" clipRule="evenodd" d="M5.59875 18.3846C7.28028 20.0769 9.57322 21 11.9426 21C14.3121 21 16.6051 20 18.3631 18.4615C21.879 14.9231 21.879 9.15385 18.3631 5.61539C16.6815 3.92308 14.3885 3 12.0191 3C9.64963 3 7.35669 3.92308 5.67516 5.61539C3.9172 7.30769 3 9.61539 3 12C3 14.3846 3.91722 16.6923 5.59875 18.3846ZM6.66874 6.61538C8.04453 5.15385 9.95538 4.38462 11.9426 4.38462C13.9299 4.38462 15.7643 5.15385 17.2166 6.61538C20.121 9.53846 20.121 14.3077 17.2166 17.2308C15.8408 18.6923 13.9299 19.4615 11.9426 19.4615C9.95538 19.4615 8.04453 18.6154 6.66874 17.2308C5.21651 15.8462 4.45225 13.9231 4.45225 11.9231C4.45225 9.92308 5.29294 8 6.66874 6.61538Z" fill="#CE9882" />
                                </svg>

                                <div>
                                    {info}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {!!showStats && (
                <div className="adpackage-featurecard-stats">
                    <div className="ad-stats">
                        <div className="ad-stats-header">
                            {header}
                        </div>
                        <div className="ad-stats-row">
                            <AdStatsCell header={t("views")} value={num_views} />
                            <AdStatsCell header={t("clicks")} value={num_press} />
                            <AdStatsCell header={"CTR"} value={num_views > 0 ? (num_press / num_views) : 0} percent={true} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// ******************************************************************************************
// ************************************* AdPackageHeader ************************************
// ******************************************************************************************
const AdPackageHeader = ({ adProject, onStateChange, uistate, children }: { adProject: AdProjectType, onStateChange(): void, uistate: AdProjectUIState, children: ReactNode }) => {

    const { t } = useTranslation();
    const f = useFormatting();
    const df = useDateFunctions();

    const [isOpen, setIsOpen] = useState<boolean>(uistate !== "finished");

    const views = DataHelper.arraySum(adProject.adLines?.map(m => (m?.num_views > 0 ? m?.num_views : 0))) +
        (adProject?.instagramstory_num_views ?? 0) +
        (adProject?.instagrampost_num_views ?? 0) +
        (adProject?.sponsoredpost_num_views ?? 0) +
        (adProject?.sponsoredblog_num_views ?? 0) +
        (adProject?.pushnotification_num_views ?? 0);

    const clicks = DataHelper.arraySum(adProject.adLines?.map(m => (m?.num_press > 0 ? m?.num_press : 0))) +
        (adProject?.instagramstory_num_press ?? 0) +
        (adProject?.instagrampost_num_press ?? 0) +
        (adProject?.sponsoredpost_num_press ?? 0) +
        (adProject?.sponsoredblog_num_press ?? 0) +
        (adProject?.pushnotification_num_press ?? 0);

    const periodName = t("periodXtoY", {
        from: f.datekeyToDate(adProject?.startdatekey),
        to: f.datekeyToDate(adProject?.enddatekey)
    });

    const numAdsValid = adProject?.adLines.filter(m => !!m?.title01)?.length;
    const numAdsTotal = adProject?.adLines?.length;
    const fNumber = useFormatting().numberDefault;

    return (
        <div>
            <div>
                {(uistate !== "finished" || true) && (
                    <div className="adpackage">
                        <AdPackageHeaderItem label={t("adPackageX", { name: adProject?.packagename })} value={adProject?.name} />
                        <AdPackageHeaderItem label={t("period")} value={periodName} />
                        <AdPackageHeaderItem label={t("deadline")} value={f.datekeyToDate(0, "-") ?? ""} />

                        <div className="last">

                            {/* draft */}
                            {(uistate === "drafting" && numAdsTotal > numAdsValid) && (
                                <div>
                                    <div className="label-inv">Förbered varje annons ({numAdsTotal - numAdsValid} kvar)</div>
                                    <div className="value-inv">När du är klar kommer vi granska att allt blivit rätt</div>
                                </div>
                            )}
                            {(uistate === "drafting" && numAdsTotal === numAdsValid) && (
                                <div>
                                    <SimpleButton
                                        onClick={onStateChange}
                                        label={t("sendforreview")}
                                        icon="check"
                                        type="primarygreen"
                                        iconRight={true}
                                    />
                                </div>
                            )}

                            {(uistate === "finished" && numAdsTotal === numAdsValid) && (
                                <div className="adpackage-summary">
                                    <div className="adpackage-summary-header">
                                        Totalt
                                    </div>

                                    <AdPackageHeaderItem label={t("views")} value={fNumber(views)?.toString()} />
                                    <AdPackageHeaderItem label={t("clicks")} value={fNumber(clicks)?.toString()} />
                                    <AdPackageHeaderItem label={"CTR"} value={DataHelper.toPercentString(clicks, views, 2) ?? "-"} />
                                </div>
                            )}
                        </div>
                    </div>

                )}
                {(uistate === "finished" && false) && (
                    <div>
                        <div className="adpackage-finished-text">
                            <div className="col-1">{adProject?.name} - {adProject?.packagename}
                            </div>
                            <div className="col-2">{periodName}</div>
                        </div>
                        <div className="adpackage-finished">
                            <div className="flex1 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>

                                <div>
                                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g filter="url(#filter0_i_3641_34316)">
                                            <rect width="56" height="56" rx="28" fill="url(#paint0_linear_3641_34316)" />
                                            <path d="M27.9997 32.065C27.7572 32.0649 27.523 31.9768 27.3407 31.817L20.3407 25.687C20.2335 25.6031 20.1446 25.4982 20.0793 25.3787C20.0141 25.2593 19.974 25.1278 19.9613 24.9923C19.9487 24.8567 19.9639 24.7201 20.0059 24.5906C20.048 24.4612 20.116 24.3417 20.2058 24.2394C20.2957 24.1372 20.4054 24.0544 20.5284 23.996C20.6513 23.9377 20.7849 23.9051 20.9209 23.9002C21.0569 23.8952 21.1925 23.9181 21.3194 23.9675C21.4462 24.0168 21.5617 24.0915 21.6587 24.187L27.9997 29.736L34.3407 24.183C34.4376 24.0875 34.5531 24.0128 34.6799 23.9635C34.8068 23.9141 34.9424 23.8912 35.0784 23.8962C35.2144 23.9011 35.348 23.9337 35.4709 23.992C35.5939 24.0504 35.7036 24.1332 35.7935 24.2354C35.8833 24.3377 35.9513 24.4572 35.9934 24.5866C36.0354 24.7161 36.0506 24.8527 36.038 24.9883C36.0253 25.1238 35.9852 25.2553 35.92 25.3747C35.8547 25.4942 35.7658 25.5991 35.6587 25.683L28.6587 31.813C28.4769 31.9742 28.2426 32.0638 27.9997 32.065Z" fill="#74584C" />
                                        </g>
                                        <defs>
                                            <filter id="filter0_i_3641_34316" x="0" y="0" width="56" height="56" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                                <feOffset dy="-1" />
                                                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                                                <feColorMatrix type="matrix" values="0 0 0 0 0.807843 0 0 0 0 0.596078 0 0 0 0 0.509804 0 0 0 0.4 0" />
                                                <feBlend mode="normal" in2="shape" result="effect1_innerShadow_3641_34316" />
                                            </filter>
                                            <linearGradient id="paint0_linear_3641_34316" x1="-3.25094e-07" y1="56" x2="56" y2="-3.25094e-07" gradientUnits="userSpaceOnUse">
                                                <stop stopColor="#FEAF9E" />
                                                <stop offset="1" stopColor="#FFC7AE" />
                                            </linearGradient>
                                        </defs>
                                    </svg>

                                </div>
                                <div>visa detaljer</div>
                            </div>
                            <div className="adpackage-finished-stat">
                                <div className="header">{t("views")}</div>
                                <div className="value">{fNumber(views)}</div>
                            </div>
                            <div className="adpackage-finished-stat">
                                <div className="header">{t("clicks")}</div>
                                <div className="value">{fNumber(clicks)}</div>
                            </div>
                            <div className="adpackage-finished-stat">
                                <div className="header">CTR</div>
                                <div className="value">{DataHelper.toPercentString(clicks, views, 2)}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {(isOpen || true) && (
                <div>
                    {children}

                    <div>

                        <ProjectAdditionalServices
                            adProject={adProject}
                            showStats={uistate === "active" || uistate === "finished"}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

const AdPackageHeaderItem = ({ label, value }: { label: string, value: string }) => {
    return (
        <div style={{ alignContent: "center" }}>
            <div className="label">
                {label}
            </div>
            <div className="value">
                {value}
            </div>
        </div>
    )
}

// ******************************************************************************************
// ***************************************** AdItem *****************************************
// ******************************************************************************************
const AdItem = ({ ad, editable, onUpdate, uistate }: { ad: AdItemType, editable: boolean, onUpdate?(adId: string, model: AdItemType, file: File | undefined): Promise<boolean>, uistate: AdProjectUIState }) => {

    const adsUI = useAdsUISettings();
    const adsUISettings = adsUI.adsUISettings;
    const { t } = useTranslation();

    // State
    const [editing, setEditing] = [
        !!adsUISettings?.editingIds?.[ad?.id],
        (e: boolean) => adsUI.updateAdsUISettingsEditingId(ad?.id, e)
    ];
    const processing = !!adsUISettings?.processing?.[ad?.id];
    const [file, setFile] = useState<File | undefined>(undefined);
    const resizeImage = useResizeImage();
    const f = useFormatting();

    // Model
    const model = adsUISettings?.editModels?.[ad?.id] ?? ad;
    const setModel = (e: AdItemType) => {
        let change = { ...adsUISettings };
        change.editModels = { ...change.editModels, [e.id]: e };
        adsUI.updateAdsUISettings({ ...change });
    }
    const removeModel = (id: string) => {
        let change = { ...adsUISettings };

        if (!!change.editModels?.[id]) {
            delete change.editModels[id];
        }
        adsUI.updateAdsUISettings({ ...change });
    }

    useEffect(() => {
        if (editing) {
            setModel({ ...ad });
            //document.getElementById(ad?.id)?.scrollIntoView({ behavior: "smooth" })
        }
        if (editing == false) {
            removeModel(ad?.id)
        }
    }, [editing])

    // Image
    const inputFile = useRef(null);
    const [imageSrc, setImageSrc] = useState<string | ArrayBuffer | null>(null);

    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        adsUI.updateAdsUISettingsProcessing(ad?.id, true)
        const f = e.target.files?.[0];
        setTimeout(() => {
            adsUI.updateAdsUISettingsProcessing(ad?.id, false);
        }, 500);
        if (!!f) {
            if (!!f) {
                if (FileReader && f) {
                    var fr = new FileReader();
                    fr.onload = () => setImageSrc(fr.result);
                    const fSmall = await resizeImage.resizeFile(f);
                    fr.readAsDataURL(fSmall);
                    setFile(fSmall);
                }
            }
        }
        adsUI.updateAdsUISettingsProcessing(ad?.id, false);
    };

    const _onUpdate = async () => {
        adsUI.updateAdsUISettingsProcessing(ad?.id, true);
        if (!!onUpdate) {
            await onUpdate(ad?.id, model, file);
        }
        adsUI.updateAdsUISettingsProcessing(ad?.id, false);
        setEditing(false);
        return true
    }
    const onCancel = async () => {
        setEditing(false);
        setImageSrc(null);
        setFile(undefined);
    }

    const adSetting = useAdsSettings().find(n => n?.type === ad?.type);

    // Scroll when returning from plants
    const r = useLocation();
    useEffect(() => {
        if (!!r.pathname?.endsWith(Config.routes.simple_ads) && editing && adsUISettings?.addingPlantsToAdId === ad?.id) {
            document.getElementById(ad?.id)?.scrollIntoView({ behavior: "smooth" })
        }
    }, [r.pathname]);



    return (
        <div
            key={ad?.id}
            id={ad?.id}
            className={"aditem aditem-type-" + ad?.type + (processing ? " processing" : "")}
        >
            <div className="ad-preview">
                <AdPreview ad={ad} imageSrc={imageSrc} />

                {/* Upload Button */}
                {editing && (
                    <>
                        <Padder heightPx={32} />
                        <SimpleButton
                            onClick={() => { (inputFile.current as any)?.click() }}
                            label={t("upload_image")}
                            type="secondary"
                            icon="image" />
                        <input
                            type='file'
                            id='file'
                            ref={inputFile}
                            style={{ display: 'none' }}
                            onChange={onFileChange}
                            accept=".jpg,.jpeg"
                        />
                        {ad?.type === "plantdetail_main_web" && (
                            <div className="plantdetail_main_web-info">
                                <div className="plantdetail_main_web-info-text">
                                    {t("plantdetailMainWebInfoText1")}
                                </div>
                                <Padder heightPx={16} />
                                <div className="plantdetail_main_web-info-header">
                                    {t("plantdetailMainWebInfoHeader")}
                                </div>
                                <Padder heightPx={8} />
                                <div className="plantdetail_main_web-info-text">
                                    {t("plantdetailMainWebInfoText2")}
                                </div>
                            </div>

                        )}
                    </>
                )}

            </div>

            {uistate === "drafting" && (
                <div className={"ad-info" + (editing ? " editing" : "")}>

                    <label className="ad-label">
                        {adSetting?.description}
                    </label>

                    {/* Not Editing */}
                    {(editing !== true) && (
                        <div>
                            <Padder heightPx={24} />
                            {/* Edit */}
                            {!!editable && (
                                <SimpleButton
                                    onClick={() => setEditing(true)}
                                    label={t("edit")}
                                    type="secondary"
                                    icon="pen"
                                    shape="none"
                                />
                            )}
                            {/* Info */}
                            {ad?.sys_datemodified_utc_epoch > 1000 && (
                                <div>
                                    <Padder heightPx={24} />
                                    <div className="text">{t("last_changed")}: </div>
                                    <div className="change">
                                        {f.epochToFriendlyDatetime(ad?.sys_datemodified_utc_epoch)}
                                    </div>
                                </div>
                            )}

                            {/* Change state */}

                        </div>
                    )}


                    {/* IS Editing */}
                    {(editing === true && ad?.type === "plantdetail_main") && (
                        <AdPlantsEditor adId={model?.id} plantIds={model?.plantIds} />
                    )}

                    {(editing === true) && (
                        <div>
                            <Padder heightPx={48} />

                            {/* Insert button here */}

                            {/* Insert button here */}

                            <LabelEditor
                                visible={!!adSetting?.title01Label}
                                title0XLabel={adSetting?.title01Label ?? "Titel 1"}
                                title0XLength={adSetting?.title01Length}
                                title0XMultiline={adSetting?.title01Multiline}
                                value={model?.title01}
                                onChange={e => setModel({ ...model, title01: e })}
                            />

                            <LabelEditor
                                visible={!!adSetting?.title02Label}
                                title0XLabel={adSetting?.title02Label ?? "Titel 2"}
                                title0XLength={adSetting?.title02Length}
                                title0XMultiline={adSetting?.title02Multiline}
                                value={model?.title02}
                                onChange={e => setModel({ ...model, title02: e })}
                            />

                            <LabelEditor
                                visible={true}
                                title0XLabel={t("ad_label_link")}
                                value={model?.link}
                                onChange={e => setModel({ ...model, link: e })}
                            />

                            {!!adSetting?.hasPriceSettings && (
                                <AdFieldToggler isOpen={!!model?.hasOfferDetails} onOpenChange={e => setModel({ ...model, hasOfferDetails: e })}>
                                    <Padder heightPx={24} />
                                    <LabelEditor
                                        visible={!!adSetting?.title03Label}
                                        title0XLabel={adSetting?.title03Label ?? "Titel 3"}
                                        title0XLength={adSetting?.title03Length}
                                        title0XMultiline={adSetting?.title03Multiline}
                                        value={model?.title03}
                                        onChange={e => setModel({ ...model, title03: e })}
                                    />

                                    <LabelEditor
                                        visible={!!adSetting?.title04Label}
                                        title0XLabel={adSetting?.title04Label ?? "Titel 4"}
                                        title0XLength={adSetting?.title04Length}
                                        title0XMultiline={adSetting?.title04Multiline}
                                        value={model?.title04}
                                        onChange={e => setModel({ ...model, title04: e })}
                                    />

                                </AdFieldToggler>
                            )}




                            <div className="button-row">
                                <SimpleButton onClick={_onUpdate} label={t("update")} type="primarygreen" icon="check" />
                                <SimpleButton onClick={onCancel} label={t("cancel")} type="beige50outlined" icon="times" />
                            </div>
                        </div>
                    )}
                </div>
            )}
            {(uistate == "active" || uistate === "finished") && (
                <div className="ad-stats">
                    <div className="ad-stats-header">
                        {adSetting?.description}
                    </div>
                    <div className="ad-stats-row">
                        <AdStatsCell header={t("views")} value={ad?.num_views} />
                        <AdStatsCell header={t("clicks")} value={ad?.num_press} />
                        <AdStatsCell header={"CTR"} value={ad?.num_views > 0 ? (ad?.num_press / ad?.num_views) : 0} percent={true} />
                    </div>
                </div>
            )}

        </div>
    )
}

const AdStatsCell = ({ header, value, percent }: { header: string, value: number, percent?: boolean }) => {

    const fn = useFormatting().numberDefault;
    const _v = value > 0 ? fn(value) : "-";
    const _vpercent = value > 0 ? Math.round(value * 10000) / 100 + "%" : "-";

    return (

        <div className="ad-stats-cell">
            <div className="header">{header}</div>
            <div className="value">{percent ? _vpercent : _v}</div>
        </div>
    )
}

const AdPlantsEditor = ({ adId, plantIds, }: { adId: string, plantIds: string }) => {

    const parsePlantIds = (e: string): { plantid: string, key: number }[] => {
        let a: string[] = [];
        if (e?.length > 10) { a = e.split(';'); }
        while (a.length < 10) { a.push(""); }
        return a.map((id, idx) => { return { plantid: id, key: idx + 1 } });
    }

    const [plants, setplants] = useState<{ plantid: string, key: number }[]>([]);

    useEffect(() => {
        setplants(parsePlantIds(plantIds))
    }, [, plantIds]);

    const db = usePlantReducer();

    useEffect(() => {
        if (plantIds?.length > 0) {
            db.loadPlantIds(plantIds?.split(";"));
            console.log("AdPlantsEditor");
        }
    }, [, adId, plantIds]);


    return (
        <div className="ad-plant-label-container">
            {plants.map(n => (
                <AdPlantsEditorTag
                    key={n?.key}
                    adId={adId}
                    plantNumber={n?.key}
                    plantid={n?.plantid}
                    allSelectedPlants={plants?.map(m => m.plantid)}
                />
            ))}
        </div>
    )
}

const AdPlantsEditorTag = ({ adId, plantNumber, plantid, allSelectedPlants }: { adId: string, plantNumber: number, plantid: string, allSelectedPlants: string[] }) => {
    const plant = usePlant(plantid);
    const hasplant = !!plantid;
    const nav = useNavigate();
    const adsUI = useAdsUISettings();
    const adsUISettings = adsUI.adsUISettings;

    const model: AdItemType = adsUISettings?.editModels?.[adId] as AdItemType;
    const plantIds = model?.plantIds;
    const plantUI = usePlantUI();

    const onClick = () => {
        if (hasplant === false) {
            adsUI.updateAdsUISettings({ addingPlantsToAdId: adId });
            plantUI.gotoAdPlantsToTarget({
                type: "ad",
                id: adId,
                plantCart: allSelectedPlants,
                cartMaxSize: 10
            });
        }
    }
    const onRemove = () => {
        // remove plant from editmodel,
        let replaced = (plantIds ?? "")?.replaceAll(plantid, "");
        replaced = replaced?.replaceAll(";;", ";");
        const m: AdItemType = { ...model, plantIds: replaced };

        let change = { ...adsUISettings };
        change.editModels = { ...change.editModels, [m.id]: m };
        adsUI.updateAdsUISettings({ ...change });
    }

    return (
        <div className={"ad-plant-label" + (hasplant ? " selected" : "")} onClick={onClick}>
            <div className="ad-plant-label-number">{plantNumber}</div>
            <div className="ad-plant-label-text">
                {hasplant && (
                    <>
                        <div className="name">
                            {plant?.name}
                        </div>

                        {!!plant?.specifictype && (
                            <div className="sort">
                                {plant?.specifictype}
                            </div>
                        )}

                        <div className="icon-remove" onClick={onRemove}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M14.7812 6.28125L11.0312 10.0312L14.75 13.75C15.0625 14.0312 15.0625 14.5 14.75 14.7812C14.4688 15.0938 14 15.0938 13.7188 14.7812L9.96875 11.0625L6.25 14.7812C5.96875 15.0938 5.5 15.0938 5.21875 14.7812C4.90625 14.5 4.90625 14.0312 5.21875 13.7188L8.9375 10L5.21875 6.28125C4.90625 6 4.90625 5.53125 5.21875 5.21875C5.5 4.9375 5.96875 4.9375 6.28125 5.21875L10 8.96875L13.7188 5.25C14 4.9375 14.4688 4.9375 14.7812 5.25C15.0625 5.53125 15.0625 6 14.7812 6.28125Z" fill="#A3B39D" />
                            </svg>
                        </div>
                    </>
                )}

                {!hasplant && (
                    <div className="none">
                        Ej vald
                    </div>
                )}
            </div>
        </div>
    )
}

const AdFieldToggler = ({ children, isOpen, onOpenChange }: { children: ReactNode, isOpen?: boolean | null | undefined, onOpenChange(e: boolean): void }) => {
    const { t } = useTranslation();
    return (
        <div className="ad-limiting-container-outer">
            <div className="ad-limiting-container">
                <div className="ad-limiting-container-toggle">
                    <ToggleSvg on={!!isOpen} onPress={onOpenChange} />
                </div>

                <div className="ad-limiting-container-text">
                    <div className="label1">
                        {t("ad_desc_highlight_title")}
                    </div>
                    <div className="label2">
                        {t("ad_desc_highlight_tagline")}
                    </div>
                </div>
            </div>
            {!!isOpen && (
                <div className="ad-limiting-container-children">
                    {children}
                </div>
            )}
        </div>
    )
}

export const ToggleSvg = ({ on, onPress }: { on: boolean, onPress(e: boolean): void }) => {
    if (!!on) {
        return (
            <svg width="40" height="24" viewBox="0 0 40 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={() => onPress(false)}>
                <g filter="url(#filter0_i_3348_38612)">
                    <path d="M0 12C0 5.37258 5.37258 0 12 0H28C34.6274 0 40 5.37258 40 12C40 18.6274 34.6274 24 28 24H12C5.37258 24 0 18.6274 0 12Z" fill="#CBDAD0" />
                    <circle cx="28" cy="12" r="8" fill="#708677" />
                </g>
                <defs>
                    <filter id="filter0_i_3348_38612" x="0" y="0" width="40" height="24" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                        <feOffset dy="1" />
                        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                        <feColorMatrix type="matrix" values="0 0 0 0 0.807843 0 0 0 0 0.596078 0 0 0 0 0.509804 0 0 0 0.4 0" />
                        <feBlend mode="normal" in2="shape" result="effect1_innerShadow_3348_38612" />
                    </filter>
                </defs>
            </svg>
        )
    } else {
        return (
            <svg width="40" height="24" viewBox="0 0 40 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={() => onPress(true)}>
                <g filter="url(#filter0_i_3348_38610)">
                    <path d="M0 12C0 5.37258 5.37258 0 12 0H28C34.6274 0 40 5.37258 40 12C40 18.6274 34.6274 24 28 24H12C5.37258 24 0 18.6274 0 12Z" fill="white" />
                    <circle cx="12" cy="12" r="8" fill="#D1C3AE" />
                </g>
                <defs>
                    <filter id="filter0_i_3348_38610" x="0" y="0" width="40" height="24" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                        <feOffset dy="1" />
                        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                        <feColorMatrix type="matrix" values="0 0 0 0 0.807843 0 0 0 0 0.596078 0 0 0 0 0.509804 0 0 0 0.4 0" />
                        <feBlend mode="normal" in2="shape" result="effect1_innerShadow_3348_38610" />
                    </filter>
                </defs>
            </svg>

        )
    }
}

export const LabelEditor = ({ visible, title0XLabel, title0XLength, title0XMultiline, value, onChange }: { visible: boolean, title0XLabel: string, title0XLength?: number, title0XMultiline?: boolean, value: string, onChange(e: string): void }) => {

    if (!visible) {
        return (null)
    }

    return (
        <div className="input-with-label">
            <label>{title0XLabel}</label>
            {!title0XMultiline && (
                <input
                    placeholder={title0XLabel}
                    className="frm-input"
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                />
            )}
            {!!title0XMultiline && (
                <textarea
                    placeholder={title0XLabel}
                    className="frm-input"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                />
            )}
        </div>
    )
}

